import { useContext, useEffect } from "react";
import { useQueryClient } from "react-query";

import { SocketContext } from "../../context/Socket/SocketContext";
import { AuthContext } from "../../context/Auth/AuthContext";
import { useWhatsAppsListQuery } from "../../queries/whatsApps";
import queryKeys from "../../queries/queryKeys";
import { isBillingRestricted } from "../../utils/billing";

const findWhatsAppIndex = (list, id) =>
  list.findIndex((item) => String(item.id) === String(id));

const mergeWhatsAppById = (collection = [], incoming) => {
  if (!incoming?.id) {
    return collection;
  }

  const index = findWhatsAppIndex(collection, incoming.id);
  if (index === -1) {
    return [incoming, ...collection];
  }

  return collection.map((item, itemIndex) =>
    itemIndex === index ? { ...item, ...incoming } : item
  );
};

const updateWhatsAppSession = (collection = [], session) => {
  const index = findWhatsAppIndex(collection, session?.id);
  if (index === -1) {
    return collection;
  }

  return collection.map((item, itemIndex) =>
    itemIndex === index
      ? {
          ...item,
          status: session.status,
          updatedAt: session.updatedAt,
          qrcode: session.qrcode,
          retries: session.retries,
          disconnectReason: session.disconnectReason,
          connectedAt: session.connectedAt,
          disconnectedAt: session.disconnectedAt,
        }
      : item
  );
};

const deleteWhatsAppById = (collection = [], whatsAppId) =>
  collection.filter((item) => String(item.id) !== String(whatsAppId));

const useWhatsApps = () => {
  const queryClient = useQueryClient();
  const socketManager = useContext(SocketContext);
  const { isAuth, user, billing, billingLoading } = useContext(AuthContext);
  const companyId = user?.companyId;
  const operationalAccess = Boolean(isAuth && companyId && billing && !billingLoading && !isBillingRestricted(user, billing));
  const whatsAppsListKey = queryKeys.whatsApps.list(companyId, { session: 0 });

  const {
    data: whatsApps = [],
    isLoading,
    isFetching,
  } = useWhatsAppsListQuery({
    companyId,
    enabled: operationalAccess,
    session: 0,
  });

  useEffect(() => {
    if (!operationalAccess || !companyId) {
      return;
    }

    const socket = socketManager.getSocket(companyId);

    const handleWhatsAppEvent = (data) => {
      if (data.action === "update") {
        queryClient.setQueryData(whatsAppsListKey, (current = []) =>
          mergeWhatsAppById(current, data.whatsapp)
        );
      }

      if (data.action === "delete") {
        queryClient.setQueryData(whatsAppsListKey, (current = []) =>
          deleteWhatsAppById(current, data.whatsappId)
        );
      }
    };

    const handleSessionEvent = (data) => {
      if (data.action === "update") {
        queryClient.setQueryData(whatsAppsListKey, (current = []) =>
          updateWhatsAppSession(current, data.session)
        );
      }
    };

    socket.on(`company-${companyId}-whatsapp`, handleWhatsAppEvent);
    socket.on(`company-${companyId}-whatsappSession`, handleSessionEvent);

    return () => {
      socket.off(`company-${companyId}-whatsapp`, handleWhatsAppEvent);
      socket.off(`company-${companyId}-whatsappSession`, handleSessionEvent);
    };
  }, [companyId, operationalAccess, queryClient, socketManager, whatsAppsListKey]);

  return {
    whatsApps: operationalAccess ? whatsApps : [],
    loading: isLoading && whatsApps.length === 0,
    isFetching,
  };
};

export default useWhatsApps;
